import type {
  Schedule,
  ScheduleEvent,
  ScheduleId,
  SwitchboardRoutingEnum,
} from '../../types'
import { getWeeklyHoursEvents, processScheduleEvents } from '../../utils'

const DEFAULT_WEEKDAY_LABELS: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
}

type WeekdayLabels = Partial<Record<number, string>>

export interface ScheduleSummaryLabels {
  hoursVaryLabel: string
  unavailableLabel: string
  weekdayLabels?: WeekdayLabels
}

export interface ScheduleListSummary {
  daysText: string
  hoursText: string
  derivedState: SwitchboardRoutingEnum
}

export interface ScheduleCardStatusValue {
  label: string
  tone?: 'open' | 'closed' | 'holiday'
}

export interface ScheduleCardStatusLabels {
  openNowLabel: string
  closedLabel: string
  holidayLabel: string
}

const toMinutes = ({
  hour,
  minute,
}: {
  hour: number
  minute: number
}): number => {
  return hour * 60 + minute
}

const formatHourMinute = ({
  hour,
  minute,
}: {
  hour: number
  minute: number
}): string => {
  const hour24 = hour
  const minutes = minute.toString().padStart(2, '0')
  const period = hour24 >= 12 ? 'pm' : 'am'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${hour12}:${minutes}${period}`
}

const sanitizeWeekdayLabel = (label?: string): string => {
  if (!label) return ''
  return label.trimEnd().replace(/[,.\u060C\u3001]+$/u, '')
}

const sanitizeDaysText = (text?: string): string => {
  if (!text) return ''
  return text.trimEnd().replace(/[,.\u060C\u3001]+$/u, '')
}

/**
 * Tries to read current routing state from schedule payload variants returned
 * by different backend endpoints.
 */
export const getScheduleStateFromPayload = (
  schedule: Schedule,
): SwitchboardRoutingEnum | undefined => {
  const payload = schedule as Schedule & {
    switchboard_status?: string | { state?: string }
    status?: string
  }

  const stateFromObject =
    typeof payload.switchboard_status === 'object'
      ? payload.switchboard_status?.state
      : undefined
  const stateFromString =
    typeof payload.switchboard_status === 'string'
      ? payload.switchboard_status
      : undefined
  const state =
    stateFromObject?.toUpperCase() ??
    stateFromString?.toUpperCase() ??
    payload.status?.toUpperCase()

  if (state === 'OPEN' || state === 'CLOSED' || state === 'HOLIDAY') {
    return state
  }

  return undefined
}

/**
 * Derives schedule-card day/time summary and current routing state from events.
 */
export const deriveScheduleListSummaryFromEvents = (
  events: ScheduleEvent[],
  scheduleTimeZone: string,
  labels: ScheduleSummaryLabels,
): ScheduleListSummary => {
  const helpers = processScheduleEvents(events, scheduleTimeZone)
  const weeklyHelpers = getWeeklyHoursEvents(helpers)

  const activeOverrideHelpers = helpers.filter(
    (helper) => helper.isOverrideEvent() && helper.isCurrentlyActive(),
  )
  const hasHolidayOverride = activeOverrideHelpers.some((helper) =>
    helper.isHolidayEvent(),
  )
  const hasClosedOverride = activeOverrideHelpers.some(
    (helper) => !helper.isHolidayEvent(),
  )

  const derivedState: SwitchboardRoutingEnum = hasHolidayOverride
    ? 'HOLIDAY'
    : hasClosedOverride
      ? 'CLOSED'
      : weeklyHelpers.some((helper) => helper.isCurrentlyActive())
        ? 'OPEN'
        : 'CLOSED'

  if (weeklyHelpers.length === 0) {
    return {
      daysText: labels.unavailableLabel,
      hoursText: labels.unavailableLabel,
      derivedState,
    }
  }

  const dayToRanges = new Map<number, Array<{ start: number; label: string }>>()

  for (const helper of weeklyHelpers) {
    const dayOfWeek = helper.zonedDateTimeStart.dayOfWeek
    const startTime = {
      hour: helper.zonedDateTimeStart.hour,
      minute: helper.zonedDateTimeStart.minute,
    }
    const endTime = {
      hour: helper.zonedDateTimeEnd.hour,
      minute: helper.zonedDateTimeEnd.minute,
    }
    const rangeLabel = `${formatHourMinute(startTime)}-${formatHourMinute(endTime)}`
    const rangesForDay = dayToRanges.get(dayOfWeek) ?? []
    rangesForDay.push({
      start: toMinutes(startTime),
      label: rangeLabel,
    })
    dayToRanges.set(dayOfWeek, rangesForDay)
  }

  const sortedDayEntries = [...dayToRanges.entries()].sort(([a], [b]) => a - b)
  const weekdayLabels = labels.weekdayLabels ?? DEFAULT_WEEKDAY_LABELS

  const daysText =
    sanitizeDaysText(
      sortedDayEntries
        .map(([day]) => sanitizeWeekdayLabel(weekdayLabels[day] ?? ''))
        .filter(Boolean)
        .join(', '),
    ) || labels.unavailableLabel

  const perDayRanges = sortedDayEntries.map(([, ranges]) =>
    ranges
      .sort((a, b) => a.start - b.start)
      .map((range) => range.label)
      .join('|'),
  )

  const uniqueRangePatterns = new Set(perDayRanges)
  const hasSingleRangePattern = uniqueRangePatterns.size === 1
  const sharedRanges = perDayRanges[0]?.split('|') ?? []
  const hasExactlyOneRange = sharedRanges.length === 1

  const hoursText =
    hasSingleRangePattern && hasExactlyOneRange
      ? sharedRanges[0]
      : labels.hoursVaryLabel

  return {
    daysText,
    hoursText,
    derivedState,
  }
}

/**
 * Build schedule summaries keyed by schedule ID.
 */
export const deriveScheduleSummariesById = (
  schedules: Schedule[],
  getEventsForSchedule: (
    schedule: Schedule,
    index: number,
  ) => ScheduleEvent[] | undefined,
  labels: ScheduleSummaryLabels,
): Map<ScheduleId, ScheduleListSummary> => {
  const summaries = new Map<ScheduleId, ScheduleListSummary>()

  schedules.forEach((schedule, index) => {
    const events = getEventsForSchedule(schedule, index)
    if (!events) return

    summaries.set(
      schedule.id,
      deriveScheduleListSummaryFromEvents(events, schedule.time_zone, labels),
    )
  })

  return summaries
}

/**
 * Resolve card status from payload and/or derived event summary.
 */
export const resolveScheduleCardStatus = (
  schedule: Schedule,
  summary: ScheduleListSummary | undefined,
  labels: ScheduleCardStatusLabels,
): ScheduleCardStatusValue => {
  const state = getScheduleStateFromPayload(schedule) ?? summary?.derivedState

  if (state === 'OPEN') {
    return {
      label: labels.openNowLabel,
      tone: 'open',
    }
  }

  if (state === 'HOLIDAY') {
    return {
      label: labels.holidayLabel,
      tone: 'holiday',
    }
  }

  return {
    label: labels.closedLabel,
    tone: 'closed',
  }
}

export const resolveScheduleCardMetaLeft = (
  summary: ScheduleListSummary | undefined,
  unavailableLabel: string,
): string => {
  return sanitizeDaysText(summary?.daysText) || unavailableLabel
}

export const resolveScheduleCardMetaRight = (
  summary: ScheduleListSummary | undefined,
  unavailableLabel: string,
): string => {
  return summary?.hoursText ?? unavailableLabel
}
